import {
  IsOptional,
  IsInt,
  IsString,
  IsDateString,
  Min,
} from 'class-validator';
import { ApiPropertyOptional } from '@nestjs/swagger';
import { Type } from 'class-transformer';

/**
 * DTO para filtrar la búsqueda de Reservas (Bookings).
 * Todas las propiedades son opcionales y se usan como query params.
 *
 * @remarks
 * Requiere decoradores de class-validator para que funcione
 * correctamente con whitelist: true en producción.
 */
export class BookingFilterDto {
  @ApiPropertyOptional({ description: 'ID del cliente', example: 66 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  clientId?: number;

  @ApiPropertyOptional({ description: 'ID del exportador', example: 10 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  exporterId?: number;

  @ApiPropertyOptional({
    description: 'Número de booking',
    example: '070ISA1205214',
  })
  @IsOptional()
  @IsString()
  bookingNumber?: string;

  @ApiPropertyOptional({
    description: 'Número de B/L',
    example: 'MEDUIQ840149',
  })
  @IsOptional()
  @IsString()
  blNumber?: string;

  @ApiPropertyOptional({
    description: 'ID del puerto de carga (POL)',
    example: 1,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  polId?: number;

  @ApiPropertyOptional({
    description: 'ID del puerto de descarga (POD)',
    example: 2,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  podId?: number;

  @ApiPropertyOptional({
    description: 'Estado de la reserva (uno o varios)',
    example: 'Confirmada',
  })
  @IsOptional()
  status?: string | string[];

  @ApiPropertyOptional({
    description: 'Fecha inicio (YYYY-MM-DD)',
    example: '2026-01-01',
  })
  @IsOptional()
  @IsDateString()
  dateFrom?: string;

  @ApiPropertyOptional({
    description: 'Fecha fin (YYYY-MM-DD)',
    example: '2026-03-31',
  })
  @IsOptional()
  @IsDateString()
  dateTo?: string;

  @ApiPropertyOptional({
    description: 'ID o nombre de temporada',
    example: '8',
  })
  @IsOptional()
  @IsString()
  season?: string;

  @ApiPropertyOptional({
    description: 'Número de página',
    example: 1,
    default: 1,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number;

  @ApiPropertyOptional({
    description: 'Elementos por página',
    example: 10,
    default: 10,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  limit?: number;

  @ApiPropertyOptional({ description: 'ID de la nave', example: 5 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  vesselId?: number;

  @ApiPropertyOptional({ description: 'ID de la naviera', example: 3 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  carrierId?: number;
}

export class BookingItemDto {
  id!: number;
  clientName!: string;
  exporterName!: string; // Renamed from exporter
  vesselName!: string;
  carrierName!: string;
  bookingNumber!: string;
  blNumber!: string;
  polName!: string;
  podName!: string;
  status!: string;
  bookingDate!: string;
  season!: string;
  versionNumber!: number;
  externalBookingNumber!: string;
  commodity!: string;
  containerCount!: number;
  voyageNumber!: string;
}

export class BookingResponseDto {
  data!: BookingItemDto[];
  total!: number;
  page?: number;
  limit?: number;
}

export class BookingPackageDto {
  id!: number;
  packageType!: string;
  quantity!: number;
  weight!: number;
  merchandise?: string;
  hsCode?: string;
}

export class BookingContainerDto {
  id!: number;
  containerNumber!: string;
  containerType!: string;
  sealNumber!: string;
  packages!: BookingPackageDto[];

  // New Fields from Screenshot 3 (Liner)
  temperature?: number;
  temperatureUnit?: string;
  ventilation?: number;
  ventilationUnit?: string;
  humidity?: number;
  coldTreatment?: string;
  controlledAtmosphere?: string;
  o2?: number;
  co2?: number;
  carrierBookingNumber?: string; // Nro. reserva (naviera)
  masterBl?: string; // Nro. B/L
}

export class BookingDetailDto extends BookingItemDto {
  // New Fields from Screenshot 1 (General) & 2 (Transporte)
  logisticService?: string; // Tipo servicio logístico
  bookingType?: string; // Tipo reserva (Liner)
  shipmentType?: string; // Tipo embarque (Contenedor)
  shipConvenience?: string; // S/ Conv
  consignee?: string; // Consignatario
  depot?: string; // Depósito
  blType?: string; // Emisión B/L
  paymentTerm?: string; // Término pago
  carrierContract?: string; // Contrato naviera
  remarks?: string; // Observaciones

  voyageId?: string; // Viaje OPB
  documentalCutOff?: string; // Corte documental
  etd?: string; // ETD
  eta?: string; // ETA
  stackingRemarks?: string; // Stacking
  lateArrivalRemarks?: string; // Late arrival

  containers!: BookingContainerDto[];
}
